import React from 'react';
import { useAuth } from '../context/AuthContext';
import { CandidateDashboardPage } from './CandidateDashboardPage';
import { CompanyDashboardPage } from './CompanyDashboardPage';

// Skeleton layout for initial auth loading - shows a clean skeleton that works for any user type
const DashboardSkeleton = () => (
  <div className="dashboard-layout">
    {/* Skeleton Sidebar - Desktop Only */}
    <aside className="hidden lg:flex fixed left-0 top-0 h-full w-72 bg-white border-r border-slate-200 p-6 z-50 flex-col">
      {/* Logo skeleton */}
      <div className="flex items-center gap-3 mb-10">
        <div className="w-10 h-10 bg-slate-200 rounded-xl animate-pulse"></div>
        <div className="h-6 w-24 bg-slate-200 rounded animate-pulse"></div>
      </div>
      {/* Nav items skeleton */}
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-xl animate-pulse">
            <div className="w-5 h-5 bg-slate-200 rounded"></div>
            <div className="h-4 w-24 bg-slate-200 rounded"></div>
          </div>
        ))}
      </div>
    </aside>

    {/* Mobile Header Skeleton */}
    <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between shadow-sm">
      <div className="flex items-center gap-2 animate-pulse">
        <div className="h-7 w-7 bg-slate-200 rounded"></div>
        <div className="h-5 w-20 bg-slate-200 rounded"></div>
      </div>
      <div className="w-6 h-6 bg-slate-200 rounded animate-pulse"></div>
    </div>

    {/* Main content skeleton */}
    <div className="main-content">
      <div className="max-w-7xl mx-auto">
        {/* Header skeleton */}
        <div className="mb-6 md:mb-10 animate-pulse">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-2 mb-2">
            <div className="h-7 md:h-9 bg-slate-200 rounded-lg w-32 md:w-40"></div>
            <div className="h-7 md:h-9 bg-slate-200 rounded-lg w-40 md:w-48"></div>
          </div>
          <div className="h-4 md:h-5 bg-slate-200 rounded w-64 md:w-80 mt-2 md:mt-3"></div>
        </div>
        
        {/* Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-4 md:p-6 animate-pulse border border-slate-100">
              <div className="flex justify-between items-start mb-3 md:mb-4">
                <div className="p-2 md:p-3 bg-slate-100 rounded-xl">
                  <div className="w-5 h-5 md:w-6 md:h-6 bg-slate-200 rounded"></div>
                </div>
                <div className="h-4 md:h-5 bg-slate-100 rounded w-16 md:w-20"></div>
              </div>
              <div className="h-5 md:h-6 bg-slate-200 rounded w-3/4 mb-2 md:mb-3"></div>
              <div className="space-y-2 mb-4 md:mb-6">
                <div className="h-3 md:h-4 bg-slate-100 rounded w-full"></div>
                <div className="h-3 md:h-4 bg-slate-100 rounded w-5/6"></div>
              </div>
              <div className="h-9 md:h-10 bg-slate-100 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const DashboardPage: React.FC = () => {
  const { user, loading } = useAuth();

  // Show skeleton with sidebar layout during auth loading
  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!user) {
    return null; // Protected route will handle redirect
  }

  const userType = user.user_metadata?.user_type;

  // Check if user is a hirer (company or recruiter)
  if (userType === 'company' || userType === 'hirer') {
    return <CompanyDashboardPage />;
  }

  return <CandidateDashboardPage />;
};
